import Vue from "vue";
import { BackEndService } from 'src/utils/BackEndService';
import { userService } from "../../user/UserService";
import { levels } from "../../user/models/UserLevelsData";
import { schoolService } from "../../school/SchoolService";
import { type AbstractPlayableContent } from "../../playable/models/AbstractPlayableContent";
import { type Version } from "../../playable/models/Version";
import { type Challenge } from "../../playable/models/Challenge";
import { type Section } from "../../playable/models/Section";
import { AvailableSkillModelLevels, type Skill } from "../../school/models/SkillModels";
import { type Video } from "../../school/models/Video";
import { type Exercise } from "../../school/models/Exercise";
import { RequestState } from "../../global/types/TechTypes";

export interface V2HomeRecommandations {
  toRevise: (Section | Challenge)[],
  musicalApplication: (Challenge | Version)[],
  toProgress: Skill[],
  inProgress: (Exercise | Version | Challenge | Video)[]
  toReviseCount?: number
}
interface HomeControllerState {
  fastToPlay: AbstractPlayableContent[],
  inProgress: AbstractPlayableContent[],
  toImprove: AbstractPlayableContent[],
  toRevise: Section[],
  pieceRecommendations: Version[],
  unseenPieceRecommendations: Version[],
  challengeRecommendations: Challenge[],
  pieceCourses: Version[],
  v2: V2HomeRecommandations,
  pieceCourseRequestState: RequestState,
  catalogHomeRequestState: RequestState
}


class HomeController {
  public readonly state = Vue.observable<HomeControllerState>({
    fastToPlay: [],
    inProgress: [],
    toImprove: [],
    toRevise: [],
    pieceRecommendations: [],
    unseenPieceRecommendations: [],
    challengeRecommendations: [],
    pieceCourses: [],
    v2: {
      toRevise: [],
      musicalApplication: [],
      toProgress: [],
      inProgress: [],
      toReviseCount: 0
    },
    pieceCourseRequestState: 'idle',
    catalogHomeRequestState: 'idle'
  })

  constructor() {
    userService.on('userService_levelUpdated', () => {
      this.loadHomeContent(true);
    })
    userService.on('userService_loggedOut', () => {
      this.resetCatalogHomeState();
      this.resetPieceCourse();
    });
  }

  loadPieceCourses(force = false) {
    if (!force && (this.state.pieceCourseRequestState === 'loading' || this.state.pieceCourseRequestState === 'loaded')) {
      return;
    }

    this.state.pieceCourseRequestState = 'loading';
    const pieceCoursePromises = userService.user.configurations.pieceCourseIds.map((versionId) => BackEndService.sendGetRequest(`version/${versionId}/sheet?inPlayer=false`))
    return Promise.all(pieceCoursePromises).then((el) => {
      this.state.pieceCourses = el.flatMap((el) => el.content.model)
      this.state.pieceCourseRequestState = 'loaded';

    })
      .catch(() => {
        this.state.pieceCourseRequestState = 'error';
      });

  }
  loadHomeContent(force = false) {
    return this.loadUserVersions(force).then((withoutReload: boolean | void) => {
      if (withoutReload) {
        return;
      }
      schoolService.loadAllModules();
      BackEndService.sendGetRequest('revisions?page=1').then(res => {
        this.state.v2.toReviseCount = res.total;
      });
    });
  }

  loadUserVersions(force = false) {
    if (!force && (this.state.catalogHomeRequestState === 'loading' || this.state.catalogHomeRequestState === 'loaded')) {
      return Promise.resolve(true);
    }

    this.state.catalogHomeRequestState = 'loading';
    if (AvailableSkillModelLevels.includes(userService.user.level.value)) {
      return BackEndService.sendGetRequest('catalog-home', undefined, 2).then((response) => {
        this.state.v2 = this.prepareHomeRecommandationsV2(response)
        this.state.catalogHomeRequestState = 'loaded';
      })
        .catch(() => {
          this.state.catalogHomeRequestState = 'error';
        });
    }

    return BackEndService.sendGetRequest('catalog-home').then((response) => {
      this.state.inProgress = response.content.in_progress.filter(this.filterSongs);
      this.state.fastToPlay = response.content.fast_to_play.filter(this.filterSongs);
      this.state.toImprove = response.content.to_improve.filter(this.filterSongs);
      this.state.pieceRecommendations = response.content.piece_recommendations.filter(this.filterSongs);
      this.state.unseenPieceRecommendations = response.content.unseen_piece_recommendations.filter(this.filterSongs);
      this.state.challengeRecommendations = response.content.challenge_recommendations.filter(this.filterSongs);
      this.state.toRevise = response.content.to_revise
      this.state.catalogHomeRequestState = 'loaded';
    })
      .catch(() => {
        this.state.catalogHomeRequestState = 'error';
      });
  }
  filterSongs = (entry: any) => {
    if (entry?.version_type && entry.version_type.includes('_DRAFT')) {
      return false;
    }
    else if (this.shouldFilterSongs() && entry.piece) {
      return entry.piece.catalog_type === 'FREE' || entry.piece.catalog_type === 'PREMIUM';
    }
    else {
      return true;
    }
  }
  shouldFilterSongs() {
    return process.env.FREE_SONG_FILTER && !userService.user.hasAccessHiddenCatalog;
  }

  resetUnseenPieceRecommendations() {
    this.state.unseenPieceRecommendations = []
  }

  get pieceCoursesTotalProgress() {
    if (this.state.pieceCourses.length === 0) {
      return 0;
    }

    const total = this.state.pieceCourses.reduce(
      (acc, piece) => acc + this.pieceProgress(piece),
      0
    );
    return total / this.state.pieceCourses.length;
  }

  pieceProgress(piece: any) {
    const pianoProgress = piece.piano_class_progress || 0;
    const consolidationProgress = piece.consolidation_class_progress || 0;
    return pianoProgress * 0.8 + consolidationProgress * 0.2;
  }

  pieceProgressPercent(piece: any) {
    return Math.round(this.pieceProgress(piece) * 100);
  }

  get levelsWithProgress() {
    return levels.filter(l => l.value !== 6).map(level => {
      const sequence = schoolService.state.sequences.find(s => s.level === level.value);
      if (!sequence) {
        return { ...level, progress: 0, progressText: "0 / 0" };
      }

      const sequenceExercice = sequence.content.reduce((acc, module) => {
        acc.totalStep += module.content.length

        if (module.school_module_progress?.max_step_index) {
          acc.finishedStep += module.school_module_progress.max_step_index + 1
        }

        return acc

      }, { totalStep: 0, finishedStep: 0 })

      return {
        ...level,
        progress: sequenceExercice.finishedStep / sequenceExercice.totalStep,
        progressText: `${sequenceExercice.finishedStep} / ${sequenceExercice.totalStep}`
      };
    });
  }

  prepareHomeRecommandationsV2(data): V2HomeRecommandations {
    const tag = (arr, from) => arr
      .filter(el => el.suggested_exercise)
      .map(el => ({ ...el, from }))
      .sort((a, b) => {
        // Prioritize items with completed_at === null
        const aCompleted = a.suggested_exercise.completed_at === null ? 0 : 1;
        const bCompleted = b.suggested_exercise.completed_at === null ? 0 : 1;
        if (aCompleted !== bCompleted) return aCompleted - bCompleted;
        
        const leftDiff = a.suggested_exercise.instance_left - b.suggested_exercise.instance_left;
        if (leftDiff !== 0) return leftDiff;

        const countDiff = a.suggested_exercise.instance_count - b.suggested_exercise.instance_count;
        if (countDiff !== 0) return countDiff;

        return a.suggested_exercise.difficulty_felt - b.suggested_exercise.difficulty_felt;
      });

    const {
      closeToFinishSkil = [],
      continueSkill = [],
      newSkill = []
    } = data.recommendations ?? {};

    const close = tag(closeToFinishSkil, 'closeToFinishSkill');
    const cont = tag(continueSkill, 'continueSkill');
    const news = tag(newSkill, 'newSkill');

    let toProgress = []

    if (close.length) {
      toProgress.push(close[0]);
    }

    if (cont.length) {
      if (close.length === 0) {
        // If close is empty, add 2 cont
        toProgress.push(cont[0]);
        if (cont.length > 1) {
          toProgress.push(cont[1]);
        }
      } else {
        toProgress.push(cont[0]);
      }
    }

    // Fill remaining slots with news
    const remainingSlots = 3 - toProgress.length;

    if (remainingSlots > 0 && news.length) {
      if (close.length === 0 && cont.length === 0) {
        // If both close and cont are empty, add 3 news
        toProgress = toProgress.concat(news.slice(0, 2));
      } else if (close.length === 0 && cont.length === 1) {
        // If close is empty and cont has only 1, add 1 news
        toProgress = toProgress.concat(news.slice(0, remainingSlots));
      } else {
        // Add remaining slots with news
        toProgress = toProgress.concat(news.slice(0, remainingSlots));
      }
    }

    return {
      toRevise: data.toRevise,
      musicalApplication: data.musicalApplication.slice(0, 4),
      toProgress,
      inProgress: data.history
    };
  }

  resetCatalogHomeState() {
    this.state.catalogHomeRequestState = 'idle';
  }
  resetPieceCourse() {
    this.state.pieceCourseRequestState = 'idle';
  }

}

export const homeController = new HomeController();
